import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Loader2,
  Pencil,
  Plus,
  Star,
  Trash2,
  Video,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTime } from '@/lib/format';
import { ItemPicker } from './item-picker';
import { cn } from '@/lib/utils';
import {
  MEDIA_TYPE,
  useAddItemMedia,
  useAddRelation,
  useCatalogFilters,
  useCreateItemGtin,
  useDeleteItemGtin,
  useItemFilters,
  useItemGtins,
  useItemLogs,
  useItemMedia,
  useItemRelations,
  useRemoveItemMedia,
  useRemoveRelation,
  useReorderItemMedia,
  useReorderRelations,
  useSetPrimaryItemGtin,
  useToggleItemFilter,
  useUpdateItemGtin,
  useUploadItemImage,
  type ItemMedia,
  type ItemRelation,
  type RelationKind,
} from './item-master.api';

/**
 * The drawers hanging off one catalogue item.
 *
 * Each is a list the item owns — its barcodes, its pictures, the filters it
 * carries, what it is recommended with or substituted by, and what has been
 * done to it. They share a shell so the six cannot drift into six ideas of the
 * same screen, which is what had happened.
 */

export function DrawerShell({
  title,
  open,
  onOpenChange,
  children,
  width = 'sm:max-w-lg',
}: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  width?: string;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn('w-full gap-0 overflow-y-auto p-0', width)}
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="space-y-3 p-4">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * The first load of a drawer's list: rows shaped like the ones it stands in
 * for, so the wait reads as the list arriving rather than as an empty box.
 */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="divide-y rounded-xl border">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 p-3">
          <Skeleton className="size-10 shrink-0 rounded" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
      {children}
    </p>
  );
}

// ------------------------------------------------------------- barcodes

export function ItemGtinsSheet({
  itemId,
  title,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const gtins = useItemGtins(open ? itemId : null);
  const create = useCreateItemGtin(itemId);
  const update = useUpdateItemGtin(itemId);
  const remove = useDeleteItemGtin(itemId);
  const setPrimary = useSetPrimaryItemGtin(itemId);

  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; gtin: string } | null>(
    null
  );

  const rows = gtins.data ?? [];
  const duplicate = rows.some((row) => row.gtin.trim() === draft.trim());
  const canAdd = !!draft.trim() && !duplicate;
  const failed = () => toast.error(t('itemMaster.actionFailed'));

  return (
    <DrawerShell title={title} open={open} onOpenChange={onOpenChange}>
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canAdd) return;
          create.mutate(draft, {
            onSuccess: () => setDraft(''),
            onError: failed,
          });
        }}
      >
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('itemMaster.newGtin')}
          aria-label={t('itemMaster.newGtin')}
          className="font-mono"
        />
        <Button type="submit" disabled={!canAdd || create.isPending}>
          {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          {t('itemMaster.add')}
        </Button>
      </form>

      {duplicate && (
        <p className="text-muted-foreground text-xs">
          {t('itemMaster.gtinAlreadyListed')}
        </p>
      )}

      {gtins.isPending && <ListSkeleton />}
      {gtins.isSuccess && rows.length === 0 && (
        <Empty>{t('itemMaster.noGtins')}</Empty>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={gtins.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              gtins.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <div key={row.id} className="flex items-center gap-2 p-3">
                {editing?.id === row.id ? (
                  <>
                    <Input
                      autoFocus
                      value={editing.gtin}
                      className="font-mono"
                      onChange={(event) =>
                        setEditing({ id: row.id, gtin: event.target.value })
                      }
                    />
                    <Button
                      size="sm"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate(
                          { gtinId: row.id, gtin: editing.gtin },
                          { onSuccess: () => setEditing(null), onError: failed }
                        )
                      }
                    >
                      {t('common.save')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 font-mono text-sm break-all">
                      {row.gtin}
                    </span>

                    {row.isPrimary ? (
                      <Badge variant="success">{t('itemMaster.primary')}</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={setPrimary.isPending}
                        onClick={() =>
                          setPrimary.mutate(row.id, { onError: failed })
                        }
                      >
                        <Star className="size-3.5" />
                        {t('itemMaster.makePrimary')}
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('itemMaster.edit')}
                      onClick={() => setEditing({ id: row.id, gtin: row.gtin })}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive"
                      aria-label={t('itemMaster.remove')}
                      // The primary is the item's identity; removing it would
                      // leave the row with none.
                      disabled={row.isPrimary || remove.isPending}
                      onClick={() => remove.mutate(row.id, { onError: failed })}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </DrawerShell>
  );
}

// ---------------------------------------------------------------- media

function MediaList({
  itemId,
  media,
  isVideo,
  busy,
}: {
  itemId: string | null;
  media: ItemMedia[];
  isVideo: boolean;
  /** The list is on screen and being refreshed. */
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const add = useAddItemMedia(itemId);
  const remove = useRemoveItemMedia(itemId);
  const reorder = useReorderItemMedia(itemId);
  const upload = useUploadItemImage();
  const fileInput = useRef<HTMLInputElement>(null);

  const [order, setOrder] = useState<ItemMedia[] | null>(null);
  const [url, setUrl] = useState('');
  const [thumbnail, setThumbnail] = useState('');

  const rows = order ?? media;
  const failed = () => toast.error(t('itemMaster.actionFailed'));
  const canAdd = !!url.trim() && (!isVideo || !!thumbnail.trim());

  function move(index: number, by: number) {
    const next = [...rows];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  return (
    <div className="space-y-3">
      {!isVideo && (
        <>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              upload.mutate(file, {
                onSuccess: (uploaded) =>
                  add.mutate(
                    { url: uploaded, type: MEDIA_TYPE.picture },
                    { onError: failed }
                  ),
                onError: () => toast.error(t('itemMaster.uploadFailed')),
              });
              event.target.value = '';
            }}
          />
          <Button
            variant="outline"
            className="w-full border-dashed"
            disabled={upload.isPending || add.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {upload.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <ImagePlus />
            )}
            {t('itemMaster.uploadImage')}
          </Button>
        </>
      )}

      <form
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canAdd) return;
          add.mutate(
            {
              url: url.trim(),
              thumbnailUrl: thumbnail.trim(),
              type: isVideo ? MEDIA_TYPE.video : MEDIA_TYPE.picture,
            },
            {
              onSuccess: () => {
                setUrl('');
                setThumbnail('');
              },
              onError: failed,
            }
          );
        }}
      >
        <div className="flex gap-2">
          <Input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder={
              isVideo ? t('itemMaster.videoUrl') : t('itemMaster.imageUrl')
            }
            aria-label={t('itemMaster.addByUrl')}
          />
          {!isVideo && (
            <Button type="submit" disabled={!canAdd || add.isPending}>
              <Plus />
            </Button>
          )}
        </div>

        {/* A video is rejected without a thumbnail, so it is asked for here
            rather than reported as a failure afterwards. */}
        {isVideo && (
          <div className="flex gap-2">
            <Input
              value={thumbnail}
              onChange={(event) => setThumbnail(event.target.value)}
              placeholder={t('itemMaster.thumbnailUrl')}
              aria-label={t('itemMaster.thumbnailUrl')}
            />
            <Button type="submit" disabled={!canAdd || add.isPending}>
              <Plus />
            </Button>
          </div>
        )}
      </form>

      {rows.length === 0 ? (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <Empty>
            {isVideo ? t('itemMaster.noVideos') : t('itemMaster.noPictures')}
          </Empty>
        </div>
      ) : (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              busy && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((entry, index) => (
              <div key={entry.id} className="flex items-center gap-2 p-2.5">
                <div className="flex shrink-0 flex-col">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-5"
                    aria-label={t('itemMaster.moveUp')}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ChevronUp className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-5"
                    aria-label={t('itemMaster.moveDown')}
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ChevronDown className="size-3.5" />
                  </Button>
                </div>

                {isVideo && !entry.thumbnailUrl ? (
                  <span className="bg-muted flex size-12 shrink-0 items-center justify-center rounded">
                    <Video className="text-muted-foreground size-5" />
                  </span>
                ) : (
                  <img
                    src={(isVideo ? entry.thumbnailUrl : entry.url) ?? ''}
                    alt=""
                    className="bg-muted size-12 shrink-0 rounded object-contain"
                  />
                )}

                <p className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                  {entry.url}
                </p>

                {!isVideo && index === 0 && (
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {t('itemMaster.mainImage')}
                  </span>
                )}

                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-destructive"
                  aria-label={t('itemMaster.remove')}
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(entry.id, {
                      onSuccess: () => setOrder(null),
                      onError: failed,
                    })
                  }
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {order && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOrder(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={reorder.isPending}
            onClick={() =>
              reorder.mutate(order, {
                onSuccess: () => {
                  toast.success(t('itemMaster.orderSaved'));
                  setOrder(null);
                },
                onError: failed,
              })
            }
          >
            {reorder.isPending && <Loader2 className="animate-spin" />}
            {t('itemMaster.saveOrder')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function ItemMediaSheet({
  itemId,
  title,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const media = useItemMedia(open ? itemId : null);

  const all = media.data ?? [];
  const pictures = all.filter((entry) => entry.type !== MEDIA_TYPE.video);
  const videos = all.filter((entry) => entry.type === MEDIA_TYPE.video);

  return (
    <DrawerShell title={title} open={open} onOpenChange={onOpenChange}>
      {media.isPending ? (
        <ListSkeleton />
      ) : media.isError ? (
        <Empty>{t('itemMaster.mediaFailed')}</Empty>
      ) : (
        <Tabs defaultValue="pictures">
          <TabsList>
            <TabsTrigger value="pictures">
              {t('itemMaster.pictures')} ({pictures.length})
            </TabsTrigger>
            <TabsTrigger value="videos">
              {t('itemMaster.videos')} ({videos.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pictures">
            <MediaList
              itemId={itemId}
              media={pictures}
              isVideo={false}
              busy={media.isFetching}
            />
          </TabsContent>
          <TabsContent value="videos">
            <MediaList
              itemId={itemId}
              media={videos}
              isVideo
              busy={media.isFetching}
            />
          </TabsContent>
        </Tabs>
      )}
    </DrawerShell>
  );
}

// -------------------------------------------------------------- filters

/** Which of the catalogue's filters this item carries. */
export function ItemFiltersSheet({
  itemId,
  title,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';

  const all = useCatalogFilters();
  const mine = useItemFilters(open ? itemId : null);
  const toggle = useToggleItemFilter(itemId);

  const carried = new Set((mine.data ?? []).map((filter) => filter.id));

  return (
    <DrawerShell title={title} open={open} onOpenChange={onOpenChange}>
      {all.isPending || mine.isPending ? (
        <ListSkeleton />
      ) : (all.data ?? []).length === 0 ? (
        <Empty>{t('itemMaster.noFilters')}</Empty>
      ) : (
        <div className="relative rounded-xl">
          <BusyOverlay show={mine.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              mine.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {(all.data ?? []).map((filter) => {
              const on = carried.has(filter.id);
              return (
                <button
                  key={filter.id}
                  type="button"
                  aria-pressed={on}
                  disabled={toggle.isPending}
                  onClick={() =>
                    toggle.mutate(filter.id, {
                      onError: () => toast.error(t('itemMaster.actionFailed')),
                    })
                  }
                  className="hover:bg-accent flex w-full items-center gap-3 p-3 text-left transition-colors"
                >
                  {filter.imageUrl ? (
                    <img
                      src={filter.imageUrl}
                      alt=""
                      className="bg-muted size-9 shrink-0 rounded object-contain"
                    />
                  ) : (
                    <span className="bg-muted size-9 shrink-0 rounded" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {(isEnglish ? filter.name : filter.nameSpa) || filter.name}
                  </span>
                  {on ? (
                    <Badge variant="success">{t('itemMaster.applied')}</Badge>
                  ) : (
                    <Badge variant="outline">{t('itemMaster.notApplied')}</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </DrawerShell>
  );
}

// ------------------------------------------ recommendations & substitutes

export function ItemRelationsSheet({
  kind,
  itemId,
  title,
  open,
  onOpenChange,
}: {
  kind: RelationKind;
  itemId: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const relations = useItemRelations(kind, open ? itemId : null);
  const remove = useRemoveRelation(kind, itemId);
  const reorder = useReorderRelations(kind, itemId);
  const add = useAddRelation(kind, itemId);

  const [picking, setPicking] = useState(false);
  const [order, setOrder] = useState<ItemRelation[] | null>(null);
  const rows = order ?? relations.data ?? [];
  const failed = () => toast.error(t('itemMaster.actionFailed'));

  function move(index: number, by: number) {
    const next = [...rows];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  return (
    <DrawerShell title={title} open={open} onOpenChange={onOpenChange}>
      <Button
        variant={picking ? 'secondary' : 'outline'}
        className="w-full"
        onClick={() => setPicking((current) => !current)}
      >
        <Plus className="size-4" />
        {picking ? t('common.cancel') : t('itemMaster.addRelation')}
      </Button>

      {/* The picker replaces the list rather than sitting above it: on a phone
          two scrolling lists in one drawer is one too many. */}
      {picking && (
        <ItemPicker
          excludeId={itemId}
          busy={add.isPending}
          onPick={(item) =>
            add.mutate(item.id, {
              onSuccess: () => {
                toast.success(t('itemMaster.saved'));
                setOrder(null);
                setPicking(false);
              },
              onError: failed,
            })
          }
        />
      )}

      {!picking && relations.isPending && <ListSkeleton />}
      {!picking && relations.isSuccess && rows.length === 0 && (
        <Empty>
          {kind === 'recommendation'
            ? t('itemMaster.noRecommendations')
            : t('itemMaster.noSubstitutes')}
        </Empty>
      )}

      {!picking && rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={relations.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              relations.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((relation, index) => (
              <div key={relation.id} className="flex items-center gap-2 p-2.5">
                <div className="flex shrink-0 flex-col">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-5"
                    aria-label={t('itemMaster.moveUp')}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ChevronUp className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-5"
                    aria-label={t('itemMaster.moveDown')}
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ChevronDown className="size-3.5" />
                  </Button>
                </div>

                {relation.itemImage ? (
                  <img
                    src={relation.itemImage}
                    alt=""
                    className="bg-muted size-12 shrink-0 rounded object-contain"
                  />
                ) : (
                  <span className="bg-muted size-12 shrink-0 rounded" />
                )}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    {[relation.brand, relation.name].filter(Boolean).join(' ')}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {relation.description}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-destructive"
                  aria-label={t('itemMaster.remove')}
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(relation.id, {
                      onSuccess: () => setOrder(null),
                      onError: failed,
                    })
                  }
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {order && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOrder(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={reorder.isPending}
            onClick={() =>
              reorder.mutate(order, {
                onSuccess: () => {
                  toast.success(t('itemMaster.orderSaved'));
                  setOrder(null);
                },
                onError: failed,
              })
            }
          >
            {reorder.isPending && <Loader2 className="animate-spin" />}
            {t('itemMaster.saveOrder')}
          </Button>
        </div>
      )}
    </DrawerShell>
  );
}

// ------------------------------------------------------------------ logs

const LOGS_PAGE_SIZE = 20;

export function ItemLogsSheet({
  itemId,
  title,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const logs = useItemLogs(open ? itemId : null, page, LOGS_PAGE_SIZE);

  const rows = logs.data?.logs ?? [];
  const total = logs.data?.count ?? 0;

  return (
    <DrawerShell
      title={title}
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setPage(0);
      }}
    >
      {logs.isPending && <ListSkeleton />}
      {logs.isError && <Empty>{t('itemMaster.logsFailed')}</Empty>}
      {logs.isSuccess && rows.length === 0 && (
        <Empty>{t('itemMaster.noLogs')}</Empty>
      )}

      {rows.length > 0 && (
        <>
          <div className="relative rounded-xl">
            <BusyOverlay show={logs.isFetching} />
            <div
              className={cn(
                'divide-y rounded-xl border',
                logs.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((log, index) => (
                <div key={log.id ?? index} className="space-y-0.5 p-3">
                  <p className="text-muted-foreground text-xs">
                    {formatDateTime(log.createdAt)}
                  </p>
                  <p className="text-sm break-words">{log.message}</p>
                </div>
              ))}
            </div>
          </div>
          <Pagination
            page={page}
            size={LOGS_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </DrawerShell>
  );
}

// ------------------------------------------------- the catalogue's filters

/** Maintaining the filter set itself, rather than one item's use of it. */
export function ManageFiltersSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const filters = useCatalogFilters();

  return (
    <DrawerShell
      title={t('itemMaster.manageFilters')}
      open={open}
      onOpenChange={onOpenChange}
    >
      {filters.isPending && <ListSkeleton />}
      {filters.isSuccess && (filters.data ?? []).length === 0 && (
        <Empty>{t('itemMaster.noFilters')}</Empty>
      )}

      {(filters.data ?? []).length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={filters.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              filters.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {filters.data!.map((filter) => (
              <div key={filter.id} className="flex items-center gap-3 p-3">
                {filter.imageUrl ? (
                  <img
                    src={filter.imageUrl}
                    alt=""
                    className="bg-muted size-10 shrink-0 rounded object-contain"
                  />
                ) : (
                  <span className="bg-muted size-10 shrink-0 rounded" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{filter.name}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {filter.nameSpa}
                  </p>
                </div>
                {/* How many items carry it — the reason to keep or retire one. */}
                <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                  {t('itemMaster.nItems', { count: filter.count ?? 0 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </DrawerShell>
  );
}

/** Shared by the drawers above and the page, so the two agree. */
export function itemLabel(item: {
  brand?: string | null;
  name?: string | null;
}) {
  return [item.brand, item.name].filter(Boolean).join(' ') || '—';
}
